import type { TimeRangePreset } from "@volca/ui/components/ui/time-range-picker";
import { AP_ASSIGNEE_ME } from "@/accounts-payable/ap-url-state";
import type { ApInvoiceListFilters } from "@/accounts-payable/query";
import type {
  ApActionType,
  ApDayField,
  ApDayRange,
} from "@/accounts-payable/types";
import { dayjs } from "@/core/utils/date";
import type { InvoiceExportKind } from "./invoice-export";
import type { InvoiceFilter } from "./use-invoices-tab";

/** Which date an export's range reads: the vendor's, or Volca's own. */
export type ExportDateField = "invoice_date" | ApDayField;

export const EXPORT_DATE_FIELDS: Record<ExportDateField, string> = {
  invoice_date: "Invoice date",
  added: "Added",
  reviewed: "Reviewed",
};

export interface InvoiceExportOptions {
  kind: InvoiceExportKind;
  dateField: ExportDateField;
  days: ApDayRange | null;
  vendorIds: string[];
  /** A key of the list's status filters. */
  status: string;
  assigneeTokens: string[];
  // The list's own search and Action filter, carried over so "export what
  // I'm looking at" stays true; the dialog names them and can drop them.
  search: string;
  actionTypes: ApActionType[];
}

/** The dialog starts from what the Invoices list is showing. */
export const initialExportOptions = (list: {
  status: string;
  dayField: ApDayField;
  dayRange: ApDayRange | null;
  assigneeTokens: string[];
  search: string;
  actionTypes: ApActionType[];
}): InvoiceExportOptions => ({
  kind: "invoices",
  // Without a day filter on the list, the range starts on the invoice date,
  // the one a bookkeeper's period is usually about.
  dateField: list.dayRange ? list.dayField : "invoice_date",
  days: list.dayRange,
  vendorIds: [],
  status: list.status,
  assigneeTokens: list.assigneeTokens,
  search: list.search.trim(),
  actionTypes: list.actionTypes,
});

export const exportListFilters = (
  options: InvoiceExportOptions,
  statusFilters: InvoiceFilter[],
  currentUserId: string | undefined,
): ApInvoiceListFilters => {
  const status = statusFilters.find(({ key }) => key === options.status);
  const days = (field: ExportDateField) =>
    options.dateField === field ? options.days : null;
  return {
    statuses: status?.statuses,
    reviewStates: status?.reviewStates,
    inQuickbooks: status?.inQuickbooks,
    search: options.search,
    actionTypes: options.actionTypes,
    assigneeIds: options.assigneeTokens
      .map((token) => (token === AP_ASSIGNEE_ME ? currentUserId : token))
      .filter((id): id is string => !!id),
    vendorIds: options.vendorIds,
    invoiceDate: days("invoice_date"),
    added: days("added"),
    reviewed: days("reviewed"),
  };
};

/** Periods a books export is usually for, from the business's today. */
export const exportPresets = (today: string): TimeRangePreset[] => {
  const day = dayjs(today, "YYYY-MM-DD");
  const preset = (
    label: string,
    from: dayjs.Dayjs,
    to: dayjs.Dayjs,
  ): TimeRangePreset => ({
    label,
    getValue: () => ({ from: from.toDate(), to: to.toDate(), label }),
  });
  const lastMonth = day.subtract(1, "month");
  const lastYear = day.subtract(1, "year");
  return [
    preset("Last 30 days", day.subtract(29, "day"), day),
    preset("This month", day.startOf("month"), day),
    preset(
      "Last month",
      lastMonth.startOf("month"),
      lastMonth.endOf("month").startOf("day"),
    ),
    preset("This year", day.startOf("year"), day),
    preset(
      "Last year",
      lastYear.startOf("year"),
      lastYear.endOf("year").startOf("day"),
    ),
  ];
};
